import React, { useState, useRef, useEffect } from 'react';
import { Plus, Calculator, ShoppingCart, Trash2, FileText, ChevronDown, Check } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import { formatGermanPrice } from 'core/utils/formatters';

const BanfActionButtons = ({
  onAddItem,
  onClearAll,
  totalAmount,
  onSave,
  saving,
  hasItems,
  itemCount,
  belegart,
  onBelegartChange,
  editMode = false,
  ociMode = false
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Belegart options
  const belegartOptions = [
    { value: 'NB', label: t('banf.docTypeNB') },
    { value: 'FO', label: t('banf.docTypeFO') },
    { value: 'RV', label: t('banf.docTypeRV') }
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleBelegartSelect = (value) => {
    onBelegartChange(value);
    setIsDropdownOpen(false);
  };

  const selectedOption = belegartOptions.find(opt => opt.value === belegart) || belegartOptions[0];

  return (
    <div className="relative group/actions mt-8">
      {/* Glass Background */}
      <div className={`absolute inset-0 rounded-3xl ${isDarkMode
        ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 border border-white/20 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.4)]'
        : 'glass-light-panel border border-slate-200'
      }`}></div>
      <div className={`absolute inset-0 rounded-3xl ${isDarkMode
        ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
        : ''
      }`}></div>
      
      <div className="relative p-6">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-4 lg:space-y-0">
          <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-6">
            {/* Liquid Glass Add Position Button - Hidden in edit mode or OCI mode */}
            {!editMode && !ociMode && (
              <button
                onClick={onAddItem}
                className={`relative group/add overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] ${
                  isDarkMode ? '' : 'bg-brand hover:bg-[#1D4ED8] text-white'
                }`}
                style={isDarkMode ? {
                  background: 'linear-gradient(135deg, rgba(147,51,234,0.6) 0%, rgba(126,34,206,0.6) 50%, rgba(107,33,168,0.6) 100%)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  boxShadow: '0 8px 32px rgba(147,51,234,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                } : {}}
              >
                {/* Smooth Hover Overlay */}
                <div
                  className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/add:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                  style={isDarkMode ? {
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 100%)'
                  } : {}}
                ></div>

                <div className="relative z-10 px-4 py-2 flex items-center font-medium text-white transition-all duration-300 drop-shadow-lg group-hover/add:drop-shadow-xl">
                  <Plus className="w-4 h-4 mr-2 transition-transform duration-300 group-hover/add:scale-110" />
                  {t('banf.addItem')}
                </div>

                {/* Smooth Wave Effect */}
                <div className={`absolute inset-0 opacity-0 group-hover/add:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                  <div
                    className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/add:translate-x-full transition-transform duration-800"
                    style={{
                      background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)'
                    }}
                  ></div>
                </div>
              </button>
            )}
            
            {/* Compact Total Sum Display */}
            <div className={`relative group/total overflow-hidden rounded-2xl transition-all duration-300 ${
              isDarkMode ? '' : 'bg-slate-100 border border-slate-200'
            }`}>
              <div className={`absolute inset-0 ${isDarkMode ? '' : 'hidden'}`}
                style={isDarkMode ? {
                  background: 'linear-gradient(135deg, rgba(16,185,129,0.6) 0%, rgba(5,150,105,0.6) 50%, rgba(4,120,87,0.6) 100%)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  boxShadow: '0 8px 32px rgba(16,185,129,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                } : {}}
              ></div>

              <div className="relative px-4 py-2 flex items-center space-x-2">
                <Calculator className={`w-4 h-4 ${isDarkMode ? 'text-white/90 drop-shadow-lg' : 'text-slate-500'}`} />
                <div className="flex flex-col">
                  <p className={`text-xs font-medium ${isDarkMode ? 'text-white/80 drop-shadow-sm' : 'text-slate-500'}`}>{t('banf.totalSum')}</p>
                  <p className={`text-sm font-bold ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-900'}`}>{formatGermanPrice(totalAmount)}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {/* Custom Belegart Dropdown - Liquid Glass Style */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`relative group/belegart overflow-hidden rounded-2xl transition-all duration-300 hover:scale-[1.01] active:scale-[0.99] ${
                  isDarkMode ? '' : 'bg-brand hover:bg-[#1D4ED8] border border-brand'
                }`}
                style={isDarkMode ? {
                  background: 'linear-gradient(135deg, rgba(59,130,246,0.6) 0%, rgba(37,99,235,0.6) 50%, rgba(29,78,216,0.6) 100%)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  boxShadow: '0 8px 32px rgba(59,130,246,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                } : {}}
              >
                {/* Hover Overlay */}
                <div
                  className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/belegart:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                  style={isDarkMode ? {
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 100%)'
                  } : {}}
                ></div>

                <div className="relative px-4 py-2 flex items-center space-x-3">
                  <div className={`relative w-8 h-8 rounded-xl flex items-center justify-center ${
                    isDarkMode ? 'bg-gradient-to-br from-white/20 via-white/10 to-transparent backdrop-blur-xl shadow-lg border border-white/20' : 'bg-white/20'
                  }`}>
                    <FileText className={`w-4 h-4 ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-white'}`} />
                  </div>
                  <div className="flex flex-col min-w-0 text-left">
                    <p className={`text-xs font-medium ${isDarkMode ? 'text-white/70 drop-shadow-sm' : 'text-white/80'}`}>{t('banf.docType')}</p>
                    <p className={`text-sm font-bold ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-white'}`} style={{ minWidth: '140px' }}>
                      {selectedOption.label}
                    </p>
                  </div>
                  <div className={`rounded-lg p-1 transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''} ${isDarkMode ? 'bg-white/20 backdrop-blur-sm' : 'bg-white/20'}`}>
                    <ChevronDown className={`w-3 h-3 ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-white'}`} />
                  </div>
                </div>
              </button>

              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div
                  className={`absolute top-full left-0 mt-2 w-full z-50 rounded-2xl overflow-hidden ${
                    isDarkMode ? '' : 'bg-white border border-slate-200 shadow-md'
                  }`}
                  style={isDarkMode ? {
                    background: 'linear-gradient(135deg, rgba(31,41,55,0.95) 0%, rgba(17,24,39,0.95) 100%)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)'
                  } : {}}
                >
                  {belegartOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleBelegartSelect(option.value)}
                      className={`w-full px-4 py-3 flex items-center justify-between transition-all duration-200 ${
                        belegart === option.value
                          ? (isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100/60')
                          : (isDarkMode ? 'hover:bg-white/10' : 'hover:bg-blue-50/60')
                      }`}
                    >
                      <span className={`text-sm font-medium ${
                        isDarkMode ? 'text-white' : 'text-slate-700'
                      }`}>
                        {option.label}
                      </span>
                      {belegart === option.value && (
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                          isDarkMode
                            ? 'bg-blue-500/30 border border-blue-400/50'
                            : 'bg-blue-500/20 border border-blue-400/50'
                        }`}>
                          <Check className={`w-3 h-3 ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`} />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Clear All Positions Button - only visible if more than 1 item and not in edit mode or OCI mode */}
            {!editMode && !ociMode && itemCount > 1 && (
              <button
                onClick={onClearAll}
                className={`relative group/clear overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] ${
                  isDarkMode ? '' : 'bg-red-600 hover:bg-red-700 text-white'
                }`}
                style={isDarkMode ? {
                  background: 'linear-gradient(135deg, rgba(239,68,68,0.6) 0%, rgba(220,38,38,0.6) 50%, rgba(185,28,28,0.6) 100%)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  boxShadow: '0 8px 32px rgba(239,68,68,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                } : {}}
              >
                {/* Smooth Hover Overlay */}
                <div
                  className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/clear:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                  style={isDarkMode ? {
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 100%)'
                  } : {}}
                ></div>

                <div className="relative z-10 px-4 py-2 flex items-center font-medium text-white transition-all duration-300 drop-shadow-lg group-hover/clear:drop-shadow-xl">
                  <Trash2 className="w-4 h-4 mr-2 transition-transform duration-300 group-hover/clear:scale-110" />
                  {t('banf.removeAll')}
                </div>

                {/* Smooth Wave Effect */}
                <div className={`absolute inset-0 opacity-0 group-hover/clear:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                  <div
                    className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/clear:translate-x-full transition-transform duration-800"
                    style={{
                      background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)'
                    }}
                  ></div>
                </div>
              </button>
            )}

            {/* Liquid Glass Add to Cart Button */}
            <button
            onClick={onSave}
            disabled={saving || !hasItems}
            className={`relative group/save overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] disabled:transform-none disabled:opacity-50 ${
              isDarkMode ? '' : 'bg-brand hover:bg-[#1D4ED8] text-white'
            }`}
            style={isDarkMode ? {
              background: 'linear-gradient(135deg, rgba(16,185,129,0.7) 0%, rgba(5,150,105,0.7) 50%, rgba(4,120,87,0.7) 100%)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.3)',
              boxShadow: '0 8px 32px rgba(16,185,129,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
            } : {}}
          >
            {/* Smooth Hover Overlay */}
            <div
              className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/save:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
              style={isDarkMode ? {
                background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 100%)'
              } : {}}
            ></div>

            <div className="relative z-10 px-6 py-2 flex items-center justify-center font-medium text-white transition-all duration-300 drop-shadow-lg group-hover/save:drop-shadow-xl">
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                  {editMode ? t('banf.updating') : t('banf.adding')}
                </>
              ) : (
                <>
                  <ShoppingCart className="w-4 h-4 mr-2 transition-transform duration-300 group-hover/save:scale-110" />
                  {editMode ? t('banf.saveChanges') : (ociMode ? t('banf.ociTransfer') : t('banf.transferToCart'))}
                </>
              )}
            </div>

            {/* Smooth Wave Effect */}
            <div className={`absolute inset-0 opacity-0 group-hover/save:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
              <div
                className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/save:translate-x-full transition-transform duration-800"
                style={{
                  background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)'
                }}
              ></div>
            </div>
          </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BanfActionButtons;